import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { ServicePause } from "../../types/servicePause";
import { formatUkDate } from "../../utils/dateUtils";

export default function ServicePauseDetail() {
  const { id } = useParams();
  const [servicePause, setServicePause] = useState<ServicePause | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchServicePauseData();
  }, [id]);

  const fetchServicePauseData = async () => {
    try {
      const response = await axiosInstance.get(`/servicepauses/${id}`);
      setServicePause(response.data);
    } catch (error) {
      setError("Failed to fetch service pause data");
      console.error("Error fetching service pause:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "—";
    return formatUkDate(dateString);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!servicePause) return <div>No data found</div>;

  return (
    <div className="space-y-6">
      <PageBreadcrumb pageTitle="Service Pause Details"  />

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Link
          to={`/service-pauses/edit/${id}`}
          className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600"
        >
          Edit
        </Link>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        <dl className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <dt className="text-sm font-medium text-gray-500">From Date</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {formatDate(servicePause.fromDate)}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">To Date</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {formatDate(servicePause.toDate)}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">From Time</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {servicePause.fromTimeString || servicePause.fromTime || "—"}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">To Time</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {servicePause.toTimeString || servicePause.toTime || "—"}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">Remarks</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {servicePause.remarks}
            </dd>
          </div>

          <div>
            <dt className="text-sm font-medium text-gray-500">Status</dt>
            <dd className="mt-1">
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                servicePause.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {servicePause.status ? 'Active' : 'Inactive'}
              </span>
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
} 